import { QuestionText, questionTextToPlainText } from 'utils/questionMarkdown';
import React from 'react';

import { Question } from 'types';
import { cn } from 'lib/utils';
import { QuestionStatusLine } from './QuestionStatusLine';

interface Props {
  question: Question;
  /** The chosen rating, or null when the learner has not chosen yet. */
  value: number | null;
  onChange: (value: number) => void;
}

const SCALE = [1, 2, 3, 4, 5, 6, 7];

const anchorClassName =
  'flex-1 relative font-body-m font-[number:var(--body-s-font-weight)] text-foundation-base-darkbase-dark-500 text-[length:var(--body-m-font-size)] tracking-[var(--body-s-letter-spacing)] leading-[var(--body-s-line-height)] [font-style:var(--body-s-font-style)]';

export const RatingQuestion: React.FC<Props> = ({ question, value, onChange }) => {
  const selected = typeof value === 'number' && value >= 1 && value <= 7 ? value : null;
  const buttonsRef = React.useRef<Array<HTMLButtonElement | null>>([]);
  const lowLabel = question.low_text ? questionTextToPlainText(question.low_text).trim() : '';
  const highLabel = question.high_text ? questionTextToPlainText(question.high_text).trim() : '';
  const groupLabel = questionTextToPlainText(question.text ?? '').trim() || 'Rating';

  const choose = (next: number) => {
    onChange(next);
    buttonsRef.current[next - 1]?.focus();
  };

  // Arrow keys move between numbers, as in a native radio group.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, position: number) => {
    const moves: Record<string, number> = {
      ArrowRight: position + 1,
      ArrowUp: position + 1,
      ArrowLeft: position - 1,
      ArrowDown: position - 1,
      Home: 1,
      End: 7,
    };
    const next = moves[event.key];
    if (next == null) return;
    event.preventDefault();
    choose(Math.min(7, Math.max(1, next)));
  };

  return (
    <div className="flex flex-col items-start relative self-stretch w-full flex-[0_0_auto]">
      <div
        role="radiogroup"
        aria-label={groupLabel}
        data-testid="rating-scale"
        className="grid w-full grid-cols-7 gap-1.5 sm:gap-2.5"
      >
        {SCALE.map((position) => {
          const isSelected = selected === position;
          const anchor = position === 1 ? lowLabel : position === 7 ? highLabel : '';
          return (
            <button
              key={position}
              ref={(element) => {
                buttonsRef.current[position - 1] = element;
              }}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={anchor ? `${position}, ${anchor}` : String(position)}
              tabIndex={isSelected || (selected == null && position === 1) ? 0 : -1}
              data-testid={`rating-option-${position}`}
              onClick={() => choose(position)}
              onKeyDown={(event) => handleKeyDown(event, position)}
              className={cn(
                'flex aspect-square min-h-11 items-center justify-center rounded-2xl border-2 font-headings-h3 text-xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foundation-orange-500 focus-visible:ring-offset-2 sm:text-2xl',
                isSelected
                  ? 'border-foundation-base-darkbase-dark-500 bg-foundation-base-darkbase-dark-500 text-white'
                  : 'border-transparent bg-white text-foundation-base-darkbase-dark-500 shadow-elevation-sm hover:border-foundation-base-darkbase-dark-100',
              )}
            >
              {position}
            </button>
          );
        })}
      </div>

      {question.low_text || question.high_text ? (
        <div className="mt-3 flex w-full items-start gap-6">
          <p className={anchorClassName}>
            {question.low_text ? <QuestionText text={question.low_text} citations={question.citations} inline /> : null}
          </p>
          <p className={cn(anchorClassName, 'text-right')}>
            {question.high_text ? <QuestionText text={question.high_text} citations={question.citations} inline /> : null}
          </p>
        </div>
      ) : null}

      {selected == null ? <QuestionStatusLine>Choose a number to continue.</QuestionStatusLine> : null}
    </div>
  );
};
